import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import { solicitarAvisosAposSucesso } from '@/lib/avisosSucesso';
import { formatarCentavos, formatarMmSs } from '@/pages/loja/utils';
import { NAVY, OURO, BORDA_CLARA } from '@/pages/loja/identidade';
import CheckAnimado from '@/components/movimento/CheckAnimado';
import NumeroAnimado from '@/components/movimento/NumeroAnimado';
import type { PedidoPublico } from '@/pages/loja/types';

const INTERVALO_POLLING_MS = 4000;
const TEMPO_PIX_MS = 30 * 60 * 1000;

interface PixLoja {
  pix_copia_e_cola: string;
  pix_qrcode_base64: string;
  expira_em?: string;
}

/** Anel com o tempo restante do Pix (na identidade escura). */
function AnelTempo({ restanteMs, totalMs }: { restanteMs: number; totalMs: number }) {
  const r = 36;
  const circ = 2 * Math.PI * r;
  const frac = Math.max(0, Math.min(1, restanteMs / totalMs));
  const offset = circ * (1 - frac);
  const critico = restanteMs < 5 * 60 * 1000;

  return (
    <svg width="88" height="88" viewBox="0 0 88 88" aria-hidden>
      <circle cx="44" cy="44" r={r} fill="none" stroke="rgba(255,255,255,.16)" strokeWidth="6" />
      <circle
        cx="44"
        cy="44"
        r={r}
        fill="none"
        stroke={critico ? '#F0B357' : OURO}
        strokeWidth="6"
        strokeLinecap="round"
        strokeDasharray={circ}
        strokeDashoffset={offset}
        style={{
          transform: 'rotate(-90deg)',
          transformOrigin: 'center',
          transition: 'stroke-dashoffset 1s linear, stroke 0.5s',
        }}
      />
      <text
        x="44"
        y="44"
        textAnchor="middle"
        dominantBaseline="central"
        fill={OURO}
        fontSize="13"
        fontWeight="bold"
        fontFamily="system-ui, sans-serif"
      >
        {formatarMmSs(restanteMs)}
      </text>
    </svg>
  );
}

type Estado = 'carregando' | 'aguardando' | 'pago' | 'cancelado' | 'nao_encontrado' | 'erro';

// Casca escura única da loja (mesmo #10203A da vitrine e da carteira).
function Tela({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-screen" style={{ background: NAVY }}>
      <div className="app-entra relative min-h-screen overflow-x-clip">{children}</div>
    </div>
  );
}

const CARTAO_ESCURO = 'rounded-[16px] border p-5';
const ESTILO_CARTAO = { borderColor: BORDA_CLARA, background: 'rgba(255,255,255,.05)' };

export default function LojaPedidoPage() {
  const { codigo } = useParams<{ codigo: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const eventoNome =
    (location.state as { eventoNome?: string | null } | null)?.eventoNome ?? null;

  const [estado, setEstado] = useState<Estado>('carregando');
  const [pedido, setPedido] = useState<PedidoPublico | null>(null);
  const [pix, setPix] = useState<PixLoja | null>(null);
  const [pixErro, setPixErro] = useState<string | null>(null);
  const [gerandoPix, setGerandoPix] = useState(false);
  const [expiraEm, setExpiraEm] = useState<number | null>(null);
  const [pixExpirado, setPixExpirado] = useState(false);
  const [copiado, setCopiado] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [refazendo, setRefazendo] = useState(false);
  const [agora, setAgora] = useState(Date.now());

  const pollRef = useRef<number | null>(null);
  const avisosRef = useRef(false);

  async function carregarPedido(): Promise<PedidoPublico | null> {
    const { data, error } = await supabase.rpc('loja_pedido_publico', { p_codigo: codigo });
    if (error) throw error;
    const p = (Array.isArray(data) ? data[0] : data) as PedidoPublico | null;
    return p ?? null;
  }

  async function gerarPix() {
    setGerandoPix(true);
    setPixErro(null);
    setPixExpirado(false);
    try {
      const { data, error } = await supabase.functions.invoke('criar-pix-loja', {
        body: { pedido_codigo: codigo },
      });
      if (error) {
        setPixErro(
          typeof error === 'string'
            ? error
            : (error as { message?: string })?.message || 'Não conseguimos gerar o Pix.',
        );
        return;
      }
      const res = data as PixLoja | null;
      if (!res?.pix_copia_e_cola) {
        setPixErro('Não conseguimos gerar o Pix. Tente de novo.');
        return;
      }
      setPix(res);
      const expMs = res.expira_em ? new Date(res.expira_em).getTime() : NaN;
      setExpiraEm(Number.isFinite(expMs) ? expMs : Date.now() + TEMPO_PIX_MS);
    } catch (e) {
      setPixErro(mensagemAmigavel(e, 'Não conseguimos gerar o Pix. Tente de novo.'));
    } finally {
      setGerandoPix(false);
    }
  }

  // Carga inicial.
  useEffect(() => {
    let ativo = true;
    async function iniciar() {
      setEstado('carregando');
      setErro(null);
      try {
        const p = await carregarPedido();
        if (!ativo) return;
        if (!p) {
          setEstado('nao_encontrado');
          return;
        }
        setPedido(p);
        if (p.status === 'pago') {
          setEstado('pago');
          return;
        }
        if (p.status === 'cancelado' || p.status === 'expirado') {
          setEstado('cancelado');
          return;
        }
        setEstado('aguardando');
        gerarPix();
      } catch (e) {
        if (ativo) {
          setErro(mensagemAmigavel(e, 'Não foi possível carregar o pedido.'));
          setEstado('erro');
        }
      }
    }
    iniciar();
    return () => {
      ativo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codigo]);

  // Polling do status do pedido.
  useEffect(() => {
    if (estado !== 'aguardando') return;
    const checar = async () => {
      try {
        const p = await carregarPedido();
        if (!p) return;
        setPedido(p);
        if (p.status === 'pago') {
          setEstado('pago');
        } else if (p.status === 'cancelado' || p.status === 'expirado') {
          setEstado('cancelado');
        }
      } catch {
        /* silencioso: próximo tick tenta de novo */
      }
    };
    pollRef.current = window.setInterval(checar, INTERVALO_POLLING_MS);
    return () => {
      if (pollRef.current) window.clearInterval(pollRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estado]);

  // Detecta expiração do Pix e atualiza o relógio a cada segundo.
  useEffect(() => {
    if (estado !== 'aguardando' || !expiraEm || pixExpirado) return;
    const checar = () => {
      setAgora(Date.now());
      if (Date.now() >= expiraEm) setPixExpirado(true);
    };
    checar();
    const id = window.setInterval(checar, 1000);
    return () => window.clearInterval(id);
  }, [estado, expiraEm, pixExpirado]);

  // Ao pagar, navega para a carteira levando o sinal de "acabou de pagar".
  useEffect(() => {
    if (estado !== 'pago' || !pedido?.carteira_codigo) return;
    const t = setTimeout(() => {
      navigate(`/carteira/${pedido.carteira_codigo}`, {
        replace: true,
        state: { recemPago: true, eventoNome },
      });
    }, 1200);
    return () => clearTimeout(t);
  }, [estado, pedido, navigate, eventoNome]);

  // Pix confirmado: uma vibrada curta, quando o aparelho permite.
  useEffect(() => {
    if (estado !== 'pago') return;
    if (typeof navigator !== 'undefined' && 'vibrate' in navigator) navigator.vibrate?.(50);
  }, [estado]);

  // Sem carteira para abrir? Pede os avisos aqui mesmo (depois do pagamento).
  useEffect(() => {
    if (estado !== 'pago' || pedido?.carteira_codigo) return;
    if (avisosRef.current) return;
    avisosRef.current = true;
    solicitarAvisosAposSucesso(eventoNome ?? undefined);
  }, [estado, pedido, eventoNome]);

  async function copiarCodigo() {
    if (!pix?.pix_copia_e_cola) return;
    try {
      await navigator.clipboard.writeText(pix.pix_copia_e_cola);
    } catch {
      /* não bloqueia */
    }
    setCopiado(true);
    if (typeof navigator !== 'undefined' && 'vibrate' in navigator) navigator.vibrate?.(30);
    setTimeout(() => setCopiado(false), 2000);
  }

  async function refazerPedido() {
    if (!pedido) return;
    setRefazendo(true);
    setErro(null);
    try {
      const itensComProduto = (pedido.itens || [])
        .filter((i) => i.produto_id != null)
        .map((i) => ({ produto_id: i.produto_id, quantidade: i.quantidade }));
      const eventoId = pedido.evento_id;

      // Se tivermos evento + itens, recria o pedido mantendo a carteira.
      if (eventoId && itensComProduto.length > 0) {
        const { data, error } = await supabase.rpc('loja_abrir_pedido', {
          p_evento: eventoId,
          p_itens: itensComProduto,
          p_nome: null,
          p_telefone: null,
          p_carteira: pedido.carteira_codigo || null,
        });
        if (error) throw error;
        const novo = (Array.isArray(data) ? data[0] : data) as { codigo?: string };
        if (novo?.codigo) {
          navigate(`/loja/pedido/${novo.codigo}`, { replace: true });
          return;
        }
      }

      // Fallback: tenta só reemitir o Pix do mesmo pedido.
      setPixExpirado(false);
      await gerarPix();
    } catch (e) {
      setErro(mensagemAmigavel(e, 'Não conseguimos refazer o pedido.'));
    } finally {
      setRefazendo(false);
    }
  }

  async function cancelarPedido() {
    try {
      await supabase.rpc('loja_cancelar_pedido_publico', { p_codigo: codigo });
    } catch {
      /* não bloqueia */
    }
    setEstado('cancelado');
  }

  const voltar = (
    <button
      type="button"
      onClick={() => navigate(-1)}
      aria-label="Voltar"
      className="aperta mb-4 flex h-10 w-10 cursor-pointer items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/15"
    >
      <i className="ri-arrow-left-line text-[20px]" aria-hidden />
    </button>
  );

  // ---- Estados de erro / não encontrado / cancelado ----
  if (estado === 'nao_encontrado' || estado === 'erro') {
    return (
      <Tela>
        <div className="mx-auto w-full max-w-[560px] px-4 py-6">
          {voltar}
          <div className={`${CARTAO_ESCURO} text-center`} style={ESTILO_CARTAO}>
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-white/[0.08] text-white/70">
              <i className={estado === 'erro' ? 'ri-error-warning-line' : 'ri-search-line'} />
            </span>
            <p className="mt-4 text-[15px] text-white/80">
              {estado === 'erro'
                ? erro || 'Não foi possível carregar.'
                : 'Pedido não encontrado.'}
            </p>
            <button
              type="button"
              onClick={() => navigate('/')}
              className="aperta mt-6 h-[52px] w-full cursor-pointer whitespace-nowrap rounded-[14px] bg-white/10 text-[15px] font-bold text-white transition-colors hover:bg-white/15"
            >
              Voltar à vitrine
            </button>
          </div>
        </div>
      </Tela>
    );
  }

  if (estado === 'cancelado') {
    return (
      <Tela>
        <div className="mx-auto w-full max-w-[560px] px-4 py-6">
          {voltar}
          <div className={`${CARTAO_ESCURO} text-center`} style={ESTILO_CARTAO}>
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-white/[0.08] text-white/70">
              <i className="ri-time-line text-2xl" />
            </span>
            <h1 className="mt-4 text-[20px] font-extrabold text-white">Pedido expirado</h1>
            <p className="mt-2 text-[14px] leading-relaxed text-white/65">
              O prazo de pagamento terminou. Você pode refazer o pedido com os mesmos itens.
            </p>
            <button
              type="button"
              onClick={refazerPedido}
              disabled={refazendo}
              className="aperta mt-6 h-[52px] w-full cursor-pointer whitespace-nowrap rounded-[14px] text-[15px] font-bold disabled:opacity-60"
              style={{ background: OURO, color: NAVY }}
            >
              {refazendo ? 'Refazendo…' : 'Refazer pedido'}
            </button>
            <button
              type="button"
              onClick={() => navigate('/')}
              className="aperta mt-2 h-[52px] w-full cursor-pointer whitespace-nowrap rounded-[14px] border border-white/15 text-[15px] font-semibold text-white/80 transition-colors hover:bg-white/[0.06]"
            >
              Voltar à vitrine
            </button>
          </div>
        </div>
      </Tela>
    );
  }

  if (estado === 'pago') {
    return (
      <Tela>
        <button
          type="button"
          onClick={() => {
            if (!pedido?.carteira_codigo) return;
            navigate(`/carteira/${pedido.carteira_codigo}`, {
              replace: true,
              state: { recemPago: true, eventoNome },
            });
          }}
          className="flex min-h-screen w-full cursor-pointer flex-col items-center justify-center px-6 text-center"
        >
          <CheckAnimado tamanho={96} cor={OURO} fundo="rgba(232,200,119,.14)" />
          <h1 className="mt-4 text-[24px] font-extrabold text-white">Pix confirmado!</h1>
          <p className="mt-1 text-[14px] text-white/65">Abrindo sua carteira…</p>
        </button>
      </Tela>
    );
  }

  // ---- Aguardando Pix ----
  const restanteMs = expiraEm ? Math.max(0, expiraEm - agora) : TEMPO_PIX_MS;
  const critico = expiraEm != null && restanteMs < 5 * 60 * 1000;

  return (
    <Tela>
      <div className="mx-auto w-full max-w-[560px] px-4 py-6">
        {voltar}

        <div className="text-center">
          <p className="text-[13px] font-semibold uppercase tracking-[0.08em]" style={{ color: OURO }}>
            Falta só pagar
          </p>
          {pedido && (
            <p className="mt-1 text-[38px] font-extrabold leading-none" style={{ color: OURO }}>
              <NumeroAnimado valor={formatarCentavos(pedido.total_centavos)} />
            </p>
          )}
          <p className="mt-2 text-[13.5px] text-white/65">
            {pedido?.comprador ? `Pedido de ${pedido.comprador}` : 'Pedido das suas fichas'}
          </p>
        </div>

        {pixErro && (
          <div className={`${CARTAO_ESCURO} mt-4`} style={ESTILO_CARTAO}>
            <p className="text-[14px] text-danger-300">{pixErro}</p>
            <button
              type="button"
              onClick={gerarPix}
              disabled={gerandoPix}
              className="aperta mt-3 h-12 w-full cursor-pointer whitespace-nowrap rounded-[12px] border border-white/15 text-[14px] font-semibold text-white transition-colors hover:bg-white/[0.06] disabled:opacity-60"
            >
              {gerandoPix ? 'Gerando…' : 'Tentar de novo'}
            </button>
          </div>
        )}

        {!pix && !pixErro && (
          <div className={`${CARTAO_ESCURO} mt-4 text-center`} style={ESTILO_CARTAO}>
            <span className="mx-auto flex h-10 w-10 items-center justify-center text-white/70">
              <i className="ri-loader-4-line animate-spin text-3xl" />
            </span>
            <p className="mt-3 text-[13px] text-white/60">Gerando seu Pix…</p>
          </div>
        )}

        {pix && !pixExpirado && (
          <>
            {/* QR grande em cartão branco */}
            <div className="mt-5 rounded-[16px] bg-white p-4">
              <div className="qr-respira mx-auto w-full max-w-[300px]">
                <img
                  src={`data:image/png;base64,${pix.pix_qrcode_base64}`}
                  alt="QR Code Pix"
                  className="block h-auto w-full"
                />
              </div>
            </div>

            {/* Botão principal: copiar o código */}
            <button
              type="button"
              onClick={copiarCodigo}
              className="aperta mt-3 flex h-[52px] w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-[14px] text-[16px] font-bold"
              style={{ background: OURO, color: NAVY }}
            >
              <span className="flex h-5 w-5 items-center justify-center">
                <i className={copiado ? 'ri-check-line' : 'ri-file-copy-line'} aria-hidden />
              </span>
              {copiado ? 'Código copiado' : 'Copiar código Pix'}
            </button>

            <p className="mt-3 text-center text-[13.5px] leading-snug text-white/70">
              Depois de pagar, suas fichas aparecem aqui sozinhas.
            </p>

            {/* Tempo que falta */}
            <div className="mt-3 flex items-center gap-4 rounded-[16px] border p-4" style={ESTILO_CARTAO}>
              <AnelTempo restanteMs={restanteMs} totalMs={TEMPO_PIX_MS} />
              <div className="min-w-0">
                <p className="text-[13.5px] font-semibold text-white">
                  {critico ? 'Falta pouco: ' : 'Este código vale por '}
                  <span style={{ color: OURO }}>{formatarMmSs(restanteMs)}</span>
                </p>
                <p className="mt-1 flex items-center gap-2 text-[12.5px] text-white/55">
                  <span className="relative flex h-2.5 w-2.5">
                    <span
                      className="absolute inline-flex h-full w-full rounded-full bg-success-400 opacity-70"
                      style={{ animation: 'aguardandoPulso 2s ease-in-out infinite' }}
                    />
                    <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-success-500" />
                  </span>
                  Aguardando o seu Pix…
                </p>
              </div>
            </div>

            <p className="mt-3 text-center text-[12.5px] text-white/45">
              Abra o app do seu banco → Pix → Ler QR Code ou cole o código.
            </p>
          </>
        )}

        {pixExpirado && (
          <div className={`${CARTAO_ESCURO} mt-4 text-center`} style={ESTILO_CARTAO}>
            <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-warning-500/15 text-warning-300">
              <i className="ri-time-line text-2xl" />
            </span>
            <h2 className="mt-3 text-[18px] font-extrabold text-white">Pix venceu</h2>
            <p className="mt-1 text-[13.5px] leading-relaxed text-white/65">
              O código expirou. Você pode refazer o pedido com os mesmos itens.
            </p>
            {erro && <p className="mt-2 text-[13px] text-danger-300">{erro}</p>}
            <button
              type="button"
              onClick={refazerPedido}
              disabled={refazendo}
              className="aperta mt-5 h-[52px] w-full cursor-pointer whitespace-nowrap rounded-[14px] text-[15px] font-bold disabled:opacity-60"
              style={{ background: OURO, color: NAVY }}
            >
              {refazendo ? 'Refazendo…' : 'Refazer pedido'}
            </button>
            <button
              type="button"
              onClick={cancelarPedido}
              className="aperta mt-2 h-[48px] w-full cursor-pointer whitespace-nowrap rounded-[14px] border border-white/15 text-[14px] font-semibold text-white/75 transition-colors hover:bg-white/[0.06]"
            >
              Cancelar pedido
            </button>
          </div>
        )}

        {/* Itens do pedido */}
        {pedido && pedido.itens.length > 0 && (
          <div className={`${CARTAO_ESCURO} mt-4`} style={ESTILO_CARTAO}>
            <p className="text-[11px] font-bold uppercase tracking-[0.08em] text-white/45">
              Seu pedido
            </p>
            <div className="mt-3 space-y-1.5">
              {pedido.itens.map((i, idx) => (
                <div key={idx} className="flex items-center justify-between gap-3 text-[14px]">
                  <span className="min-w-0 truncate text-white/80">
                    {i.quantidade}× {i.nome}
                  </span>
                  {i.preco_centavos != null && (
                    <span className="shrink-0 font-semibold" style={{ color: OURO }}>
                      {formatarCentavos(i.preco_centavos * i.quantidade)}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <style>{`
        @media (prefers-reduced-motion: no-preference) {
          @keyframes aguardandoPulso {
            0%, 100% { opacity: 0.4; }
            50% { opacity: 1; }
          }
        }
        @media (prefers-reduced-motion: reduce) {
          @keyframes aguardandoPulso {
            0%, 100% { opacity: 1; }
          }
        }
      `}</style>
    </Tela>
  );
}